// src/components/hotelRatings/FormAddEditHotelRating.jsx

import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Stack, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import useCreateHotelRating from "../../hooks/hotelRatings/useCreateHotelRating";
import useUpdateHotelRating from "../../hooks/hotelRatings/useUpdateHotelRating";
import FormInput from "../inputs/FormInput";
import i18next from "i18next";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditHotelRating = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const showArabic = activeLang === "ar";
    const showEnglish = activeLang === "en";

    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    const [nameAr, setNameAr] = useState(currentData?.name_ar || "");
    const [nameEn, setNameEn] = useState(currentData?.name_en || "");
    const [stars, setStars] = useState(currentData?.stars || 5);
    const [isActive, setIsActive] = useState(currentData?.is_active ?? true);

    const { createHotelRatings, isPending: isCreating } = useCreateHotelRating();
    const { editHotelRatings, isUpdate } = useUpdateHotelRating();

    const loadingSubmit = isCreating || isUpdate;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setStars(currentData?.stars || 5);
            setIsActive(currentData?.is_active ?? true);
        }
    }, [isEdit, currentData]);

    const SubmitForm = (e) => {
        e.preventDefault();
        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            stars: parseInt(stars),
            is_active: isActive,
        };
        if (isEdit && currentData?.id) {
            editHotelRatings(
                { id: currentData.id, req: payload },
                {
                    onSuccess: () => {
                        if (onSubmitSuccess) onSubmitSuccess();
                    },
                }
            );
        } else {
            createHotelRatings(payload, {
                onSuccess: () => {
                    if (onSubmitSuccess) onSubmitSuccess();
                },
            });
        }
    };

    // Status options for FormInput select
    const statusOptions = [
        { id: true, name: isArabic ? "نشط" : "Active" },
        { id: false, name: isArabic ? "معطل" : "Disabled" },
    ];

    const isActiveValue = isActive === true || isActive === "true" || isActive === 1;

    // Stars options for select
    const starsOptions = [
        { id: 1, name: "1" },
        { id: 2, name: "2" },
        { id: 3, name: "3" },
        { id: 4, name: "4" },
        { id: 5, name: "5" },
    ];

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box
                component="form"
                onSubmit={SubmitForm}
                sx={{
                    p: { xs: 2, md: 4 },
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography variant="h6">
                        {isEdit
                            ? isArabic
                                ? "تعديل تصنيف الفندق"
                                : "Edit Hotel Rating"
                            : isArabic
                            ? "إضافة تصنيف فندق جديد"
                            : "Create New Hotel Rating"}
                    </Typography>

                    <LanguageToggle
                        activeLang={activeLang}
                        onChange={(lng) => setActiveLang(lng)}
                        showLabel={true}
                    />
                </Box>

                <Box mb={3}>
                    <Grid container spacing={2}>
                        {showArabic && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="الاسم (عربي)"
                                    placeholder="أدخل اسم التصنيف بالعربي"
                                    type="text"
                                    name="name_ar"
                                    value={nameAr}
                                    onChange={(e) => setNameAr(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        {showEnglish && (
                            <Grid item xs={12} md={6}>
                                <FormInput
                                    label="Name (English)"
                                    placeholder="Enter rating name in English"
                                    type="text"
                                    name="name_en"
                                    value={nameEn}
                                    onChange={(e) => setNameEn(e.target.value)}
                                    required
                                />
                            </Grid>
                        )}

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "عدد النجوم" : "Stars"}
                                type="select"
                                name="stars"
                                value={stars}
                                onChange={(e) => setStars(e.target.value)}
                                options={starsOptions}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <FormInput
                                label={isArabic ? "الحالة" : "Status"}
                                type="select"
                                name="is_active"
                                value={isActiveValue}
                                onChange={(e) => setIsActive(e.target.value === true || e.target.value === "true" || e.target.value === 1)}
                                options={statusOptions}
                                required
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box mt={3} display="flex" justifyContent="flex-end" gap={1}>
                    <Stack direction="row" spacing={1}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loadingSubmit}
                        >
                            {loadingSubmit
                                ? isArabic
                                    ? "جارٍ الحفظ..."
                                    : "Saving..."
                                : isEdit
                                ? isArabic
                                    ? "تحديث"
                                    : "Update"
                                : isArabic
                                ? "حفظ"
                                : "Save"}
                        </Button>
                    </Stack>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditHotelRating;
